// The calendar's API surface.
//
// ITS OWN MODULE, not a block appended to api/index.js. That file is already past the
// 300-line ceiling, and client/CLAUDE.md describes this folder as "fetch wrappers, one
// module per resource" — which is what this is. The calendar pages import from here
// directly; nothing else in the app needs it.
import { get, post, put, del } from './http';

export const meetingsApi = {
  // THE ONE READ every view is built from. Day, week and month differ only in the window
  // they ask for — the same rows, three framings — and the mine/partner filter is applied
  // in the browser over what comes back. Switching who you are looking at is a way of
  // reading one window, not a different question, so it must not cost a round trip.
  window: (from, to) => get(`/meetings?from=${from}&to=${to}`),

  // THE REVERSE VIEW: one client's whole history of meetings, for their card. The calendar
  // answers "what is on this date"; this answers "when do we next see them, and what was
  // agreed last time".
  //
  // Comes back ALREADY GROUPED and ordered — { upcoming, past, cancelled }, soonest-first
  // in the first and newest-first in the others — because where a meeting sits in time is
  // decided by the same date rule everything else in this system is judged by
  // (calculations/dueState), and that is the server's to own. The tab renders the groups it
  // is handed and sorts nothing.
  byClient: (clientId) => get(`/meetings/client/${clientId}`),

  // EVERY MEETING, ANY DATE, by what it was about (077): title, client, the outside person,
  // the job and the summary. Newest first, at most 50. Two characters at least.
  search: (q) => get(`/meetings/search?q=${encodeURIComponent(q)}`),

  // A meeting with someone outside the system, linked to the client or lead they became
  // (077): what would be linked (the follow-up chain, and same-name meetings to tick), then
  // the link itself.
  linkCandidates: (id) => get(`/meetings/${id}/link-candidates`),
  link: (id, body) => post(`/meetings/${id}/link`, body),

  // WHICH TIMES ARE ACTUALLY OFFERABLE on one day, for ONE partner. Computed on the
  // server, never in the browser: free/busy is a rule, and the client must not hold a
  // second opinion about it.
  //
  //   partnerId  whose diary. Changing the owner in the form refetches, so you cannot
  //              book into the other partner's clash by switching the owner last.
  //   withId     the OTHER partner of an internal meeting. The server then returns the
  //              times free in BOTH diaries — the intersection is computed there, not by
  //              fetching two grids here and overlapping them, which would be the browser
  //              holding an opinion about free/busy.
  //   duration   a 60-minute meeting cannot start at 23:30 even though 23:30 is free.
  //   excludeId  the meeting being EDITED. Without it a meeting clashes with itself and
  //              no booking could ever keep its time.
  slots: (date, partnerId, duration, excludeId, withId) => {
    const q = new URLSearchParams({ date, duration: String(duration) });
    if (partnerId) q.set('partner_id', String(partnerId));
    if (withId) q.set('with', String(withId));
    if (excludeId) q.set('exclude', String(excludeId));
    return get(`/meetings/slots?${q.toString()}`);
  },

  create: (body) => post('/meetings', body),
  update: (id, body) => put(`/meetings/${id}`, body),

  // Handing a meeting to the other partner. Its own call, not a field on `update`,
  // because it is its own act: it writes one column and it is the only edit that has to
  // ask a DIFFERENT person's diary — a meeting handed into a clash is refused.
  setOwner: (id, ownerPartnerId) => put(`/meetings/${id}/owner`, { owner_partner_id: ownerPartnerId }),

  // What actually happened: how long it really ran, and what was said. Text is required;
  // the duration is optional, because "about the scheduled hour" is a real answer.
  setSummary: (id, body) => put(`/meetings/${id}/summary`, body),

  // Called off, or reinstated. A cancelled meeting FREES ITS SLOT and stays on the
  // calendar marked as cancelled — reinstating it re-books that slot, so it can be
  // refused if somebody took it meanwhile.
  setStatus: (id, status, reason) => put(`/meetings/${id}/status`, { status, reason }),

  // Permanent, and takes nothing with it: a meeting is a leaf row with no child rows and
  // no stored file. For the booking that should never have existed — a meeting that was
  // called off is cancelled instead, which keeps the record that it was ever arranged.
  remove: (id) => del(`/meetings/${id}`),
};

export default meetingsApi;
